import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { HOST } from 'config/config';
import useToast from 'services/Hooks/useToast';

export const useSupportChats = () => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['supportChats'],
    queryFn: async () => {
      const response = await axios.get(HOST + '/api/dealer-portal/support-chats');
      return response.data;
    },
    staleTime: 0,
    throwOnError: errorMessage,
  });
  return query;
};

export const usePostGeneralSupportChat = isDemo => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postGeneralSupportChat'],
    mutationFn: async supportChat => {
      return await axios.post(
        `${HOST}/api/dealer-portal/support-chats/add/general`,
        isDemo ? { ...supportChat, isDemo } : supportChat,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useCartSupportChats = cartId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['cartSupportChats', cartId],
    queryFn: async () => {
      const response = await axios.get(`${HOST}/api/dealer-portal/support-chats/cart/${cartId}`);
      return response.data;
    },
    staleTime: 0,
    throwOnError: errorMessage,
    enabled: !!cartId,
  });
  return query;
};

export const usePostCartSupportChat = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postCartSupportChat'],
    mutationFn: async cartSupportChat => {
      return await axios.post(`${HOST}/api/dealer-portal/support-chats/add/cart`, cartSupportChat);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useExteriorSignageQuoteSupportChats = exteriorSignageQuoteId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['exteriorSignageQuoteSupportChats', exteriorSignageQuoteId],
    queryFn: async () => {
      const response = await axios.get(
        `${HOST}/api/dealer-portal/support-chats/exterior-signage-quote/${exteriorSignageQuoteId}`,
      );
      return response.data;
    },
    staleTime: 0,
    throwOnError: errorMessage,
    enabled: !!exteriorSignageQuoteId,
  });
  return query;
};

export const usePostExteriorSignageQuoteSupportChat = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postExteriorSignageQuoteSupportChat'],
    mutationFn: async exteriorSignageQuoteSupportChat => {
      return await axios.post(
        `${HOST}/api/dealer-portal/support-chats/add/exterior-signage-quote`,
        exteriorSignageQuoteSupportChat,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useMerchandisingProgramQuoteSupportChats = merchandisingProgramQuoteId => {
  const { errorMessage } = useToast();
  const query = useQuery({
    queryKey: ['merchandisingProgramQuoteSupportChats', merchandisingProgramQuoteId],
    queryFn: async () => {
      const response = await axios.get(
        `${HOST}/api/dealer-portal/support-chats/merchandising-program-quote/${merchandisingProgramQuoteId}`,
      );
      return response.data;
    },
    staleTime: 0,
    throwOnError: errorMessage,
    enabled: !!merchandisingProgramQuoteId,
  });
  return query;
};

export const usePostMerchandisingProgramQuoteSupportChat = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postMerchandisingProgramQuoteSupportChat'],
    mutationFn: async merchandisingProgramQuoteSupportChat => {
      return await axios.post(
        `${HOST}/api/dealer-portal/support-chats/add/merchandising-program-quote`,
        merchandisingProgramQuoteSupportChat,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteSupportChat = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteSupportChat'],
    mutationFn: async supportChat => {
      return await axios.delete(`${HOST}/api/dealer-portal/support-chats/${supportChat.id}`);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostSupportChatReply = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postSupportChatReply'],
    mutationFn: async supportChatReply => {
      return await axios.post(
        `${HOST}/api/dealer-portal/support-chats/${supportChatReply.supportChat_Id}/reply`,
        supportChatReply,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const usePostMarkAsReadSupportChat = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['postMarkAsReadSupportChat'],
    mutationFn: async supportChatId => {
      return await axios.post(`${HOST}/api/dealer-portal/support-chats/${supportChatId}/mark-as-read`);
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
      queryClient.invalidateQueries({ queryKey: ['approval-notification'] });
      queryClient.invalidateQueries({ queryKey: ['dealerSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useDeleteSupportChatReply = () => {
  const { saveMessage, errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['deleteSupportChatReply'],
    mutationFn: async supportChatReply => {
      return await axios.delete(
        `${HOST}/api/dealer-portal/support-chats/${supportChatReply.supportChat_Id}/reply/${supportChatReply.id}`,
      );
    },
    onSuccess: () => {
      saveMessage();
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useUpdateSupportChatFiles = () => {
  const { errorMessage } = useToast();
  const queryClient = useQueryClient();
  const query = useMutation({
    mutationKey: ['updateSupportChatLogo'],
    mutationFn: async supportChatInfo => {
      var formData = new FormData();
      supportChatInfo.files.forEach(f => formData.append(`files`, f));

      formData.append('type', supportChatInfo.type);

      return await axios.post(`${HOST}/api/dashboard/attached-files/${supportChatInfo.entityId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supportChats'] });
      queryClient.invalidateQueries({ queryKey: ['cartSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['exteriorSignageQuoteSupportChats'] });
      queryClient.invalidateQueries({ queryKey: ['merchandisingProgramQuoteSupportChats'] });
    },
    onError: errorMessage,
  });
  return query;
};

export const useDownloadSupportChatFile = () => {
  const { errorMessage } = useToast();
  const query = useMutation({
    mutationKey: ['download'],
    mutationFn: async supportChatInfo => {
      return await axios.get(
        `${HOST}/api/dashboard/attached-files/${supportChatInfo.entityId}/${supportChatInfo.fileId}/download`,
        {
          params: {
            type: supportChatInfo.type,
          },
          responseType: 'blob', // important
        },
      );
    },

    onError: errorMessage,
  });
  return query;
};
